import React, { useEffect, useState } from 'react';
import { X, Gift, Percent, Tag, Coins, CalendarDays } from 'lucide-react';

interface DiscountRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reward: DiscountReward) => void;
  editingReward?: DiscountReward | null;
  products?: { name: string; description: string }[];
}

export interface DiscountReward {
  id?: string;
  discountType: 'percentage' | 'credit_points' | 'free_months';
  discountValue: number;
  productName?: string;
}

const DISCOUNT_TYPES = [
  { value: 'percentage' as const, label: 'Percentage', icon: Percent, suffix: '%' },
  { value: 'credit_points' as const, label: 'Credit Points', icon: Coins, suffix: 'pts' },
  { value: 'free_months' as const, label: 'Free Months', icon: CalendarDays, suffix: 'mo' },
];

const createEmptyDiscountReward = (): DiscountReward => ({
  discountType: 'percentage',
  discountValue: 0,
  productName: '',
});

export const buildDiscountRewardName = (reward: DiscountReward) => {
  let amountLabel: string;
  switch (reward.discountType) {
    case 'percentage':
      amountLabel = `${reward.discountValue}% off`;
      break;
    case 'credit_points':
      amountLabel = `${reward.discountValue} credit points`;
      break;
    case 'free_months':
      amountLabel = `${reward.discountValue} free month${reward.discountValue !== 1 ? 's' : ''}`;
      break;
    default:
      amountLabel = `${reward.discountValue}% off`;
  }

  return reward.productName?.trim()
    ? `${amountLabel} on ${reward.productName.trim()}`
    : amountLabel;
};

const DiscountRewardModal: React.FC<DiscountRewardModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingReward,
  products,
}) => {
  const [formData, setFormData] = useState<DiscountReward>(editingReward || createEmptyDiscountReward());

  useEffect(() => {
    if (!isOpen) return;
    setFormData(editingReward || createEmptyDiscountReward());
  }, [editingReward, isOpen]);

  if (!isOpen) return null;

  const handleChange = (field: keyof DiscountReward, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (formData.productName?.trim() && formData.discountValue > 0) {
      onSave(formData);
      onClose();
    }
  };

  const canSubmit = () => {
    return Boolean(formData.productName?.trim()) && formData.discountValue > 0;
  };

  const activeType = DISCOUNT_TYPES.find(t => t.value === formData.discountType) || DISCOUNT_TYPES[0];

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {editingReward ? 'Edit Discount Reward' : 'Create Discount Reward'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product <span className="text-red-500">*</span>
              </label>
              {products && products.length > 0 ? (
                <select
                  value={formData.productName || ''}
                  onChange={(e) => handleChange('productName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Select a product</option>
                  {products.map((product, idx) => (
                    <option key={idx} value={product.name}>{product.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={formData.productName || ''}
                  onChange={(e) => handleChange('productName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter a product name"
                />
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Type <span className="text-red-500">*</span>
                </label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  {DISCOUNT_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isActive = formData.discountType === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => {
                          handleChange('discountType', type.value);
                          if (type.value === 'free_months') {
                            handleChange('discountValue', Math.max(1, Math.floor(formData.discountValue)));
                          }
                        }}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-orange-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        } ${type.value !== 'percentage' ? 'border-l border-gray-300' : ''}`}
                      >
                        <Icon className="w-4 h-4" />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.discountType === 'percentage' ? 'Discount Value' : formData.discountType === 'credit_points' ? 'Credit Points' : 'Number of Months'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <activeType.icon className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => {
                      const val = formData.discountType === 'free_months'
                        ? Math.max(0, Math.floor(parseFloat(e.target.value) || 0))
                        : parseFloat(e.target.value) || 0;
                      handleChange('discountValue', val);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                    max={formData.discountType === 'percentage' ? 100 : undefined}
                    step={formData.discountType === 'free_months' ? 1 : undefined}
                  />
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Tag className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-orange-900 mb-1">Preview</h4>
                  <p className="text-sm text-orange-800">
                    {buildDiscountRewardName(formData)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit()}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Gift className="w-5 h-5" />
                <span>{editingReward ? 'Update Reward' : 'Create Reward'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DiscountRewardModal;
